import { useEffect, useRef, useCallback } from "react";
import { saveAbandonedCheckout, completeAbandonedCheckout } from "@/lib/abandoned-checkout.functions";

interface AbandonedCheckoutData {
  email: string;
  fullName?: string;
  phone?: string;
  isGift?: boolean;
  referralCode?: string | null;
}

const getSessionId = (): string => {
  const key = "checkout_session_id";
  // SSR has no sessionStorage - fall back to a throwaway id on the server.
  if (typeof window === "undefined") return crypto.randomUUID();
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
};

export const useAbandonedCheckout = () => {
  const savedRef = useRef(false);
  const sessionId = useRef(getSessionId());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const saveCheckoutStart = useCallback((data: AbandonedCheckoutData) => {
    if (!data.email || data.email.length < 5) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        const result = await saveAbandonedCheckout({
          data: {
            sessionId: sessionId.current,
            email: data.email.slice(0, 254),
            isGift: data.isGift || false,
            ...(data.fullName ? { fullName: data.fullName.slice(0, 100) } : {}),
            ...(data.phone ? { phone: data.phone.slice(0, 30) } : {}),
            ...(data.referralCode ? { referralCode: data.referralCode.slice(0, 20) } : {}),
          },
        });

        if (result.ok) savedRef.current = true;
      } catch {
        // Silent fail - don't interrupt checkout flow
      }
    }, 2000);
  }, []);

  const markCompleted = useCallback(async () => {
    if (!savedRef.current) return;
    try {
      await completeAbandonedCheckout({
        data: { sessionId: sessionId.current },
      });
    } catch {
      // Silent fail
    }
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { saveCheckoutStart, markCompleted };
};
